import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { surveyAnalyzeService } from '@/service/api/survey-analyze.service';
import { Event } from '@/models/jgrow/event';
import { RootState, store } from '../..';
import {
  ActiveEntryMatrix,
  MetaAwarenessGrowthQuestion,
  SurveyActivityAnswer,
  SurveyAnswer,
  SurveyCompetencyAnswer,
  SurveyCompetencySelectActivity,
  UserSurveyWithActiveEntryMatrix,
} from '@/models/jgrow/survey-answer';
import { Activity } from '@/models/activity/activity';
import { SurveyAnalysisTabelMapping } from '@/models/jgrow/survey-analysis-tabel-mapping';
import { Competency } from '@/models/competency/competency';
import { JgrowEventLikertScale } from '@/models/jgrow/jgrow-event-likert-scale';

export interface ISurveyAnalyzeModule {
  eventID: number;
  event: Event;
  answers: SurveyAnswer[];
}
@Module({ dynamic: true, store, name: 'surveyAnalyzeModule', namespaced: true })
class SurveyAnalyzeModule extends VuexModule implements ISurveyAnalyzeModule {
  eventID: number = 0;
  event: Event = new Event();
  totalUser: number = 0;
  answers: SurveyAnswer[] = [];
  activeEntryMatrices: ActiveEntryMatrix[] = [];
  competencyPlusMatrices: ActiveEntryMatrix[] = [];
  activities: Activity[] = [];
  competencies: Competency[] = [];
  metaAwarenessGrowthQuestions: MetaAwarenessGrowthQuestion[] = [];
  beforeEvalSetting: number = 0;
  afterEvalSetting: number = 0;
  likertScales: JgrowEventLikertScale[] = [];

  activityNameMap: Map<number, string> = new Map();
  metaAwarenessNameMap: Map<number, string> = new Map();
  competencyNameMap: Map<number, string> = new Map();
  surveyAnalysisSelectTabelMap: Map<number, SurveyAnalysisTabelMapping[]> = new Map();

  competencyPlusMatrixMap: Map<number, ActiveEntryMatrix[]> = new Map();

  @Mutation
  setEventID(value: number) {
    this.eventID = value;
  }

  @Mutation
  setEvent(value: Event) {
    this.event = value;
  }

  @Mutation
  setTotalUser(value: number) {
    this.totalUser = value;
  }

  @Mutation
  setAnswers(value: SurveyAnswer[]) {
    this.answers = value;
  }

  @Mutation
  setActivities(value: Activity[]) {
    this.activities = value;
  }

  @Mutation
  setCompetencies(value: Competency[]) {
    this.competencies = value;
  }

  @Mutation
  setMetaAwarenessGrowthQuestions(value: MetaAwarenessGrowthQuestion[]) {
    this.metaAwarenessGrowthQuestions = value;
  }

  @Mutation
  setActivityNameMap(value: Map<number, string>) {
    this.activityNameMap = value;
  }

  @Mutation
  setMetaAwarenessNameMap(value: Map<number, string>) {
    this.metaAwarenessNameMap = value;
  }

  @Mutation
  setCompetencyNameMap(value: Map<number, string>) {
    this.competencyNameMap = value;
  }

  @Mutation
  setSurveyAnalysisSelectTabelMap(value: Map<number, SurveyAnalysisTabelMapping[]>) {
    this.surveyAnalysisSelectTabelMap = value;
  }

  @Mutation
  setActiveEntryMatrices(value: ActiveEntryMatrix[]) {
    this.activeEntryMatrices = value;
  }

  @Mutation
  setCompetencyPlusMatrices(value: ActiveEntryMatrix[]) {
    this.competencyPlusMatrices = value;
  }

  @Mutation
  setBeforeEvalSetting(value: number) {
    this.beforeEvalSetting = value;
  }

  @Mutation
  setAfterEvalSetting(value: number) {
    this.afterEvalSetting = value;
  }

  @Mutation
  setCompetencyPlusMatrixMap(value: Map<number, ActiveEntryMatrix[]>) {
    this.competencyPlusMatrixMap = value;
  }

  @Mutation
  setLikertScale(value: JgrowEventLikertScale[]) {
    this.likertScales = value;
  }

  @Action
  async getEventInfo() {
    this.setEvent(await surveyAnalyzeService.getEvent(this.eventID));
  }

  @Action
  async fetchSurveyAnswers() {
    const userSurveyMatrix: UserSurveyWithActiveEntryMatrix = await surveyAnalyzeService.getAnswers(this.eventID);
    this.setAnswers(userSurveyMatrix.userSurveyAnalysis);
    if (userSurveyMatrix.activeEntryMatrices) this.activeEntryMatrices.sort((a, b) => a.activityID - b.activityID);

    this.setActiveEntryMatrices(userSurveyMatrix.activeEntryMatrices);
    this.setTotalUser(userSurveyMatrix.totalUser);

    this.setActivities(userSurveyMatrix.activities);
    this.setCompetencies(userSurveyMatrix.competencies);
    this.setMetaAwarenessGrowthQuestions(userSurveyMatrix.metaAwarenessGrowthQuestions);
    this.makeActivityMap(userSurveyMatrix.activities);
    this.makeCompetencyMap(userSurveyMatrix.competencies);
    this.makeMetaAwarenessNameMap(userSurveyMatrix.metaAwarenessGrowthQuestions);
    this.setLikertScale(userSurveyMatrix.likertScales);
  }

  @Action({ rawError: true })
  async getAggressiveEntryRateTabels(): Promise<Map<number, SurveyAnalysisTabelMapping>> {
    const allActivityAnswers: SurveyActivityAnswer[] = new Array();
    // 事後のみ
    const type: number = 2;
    const AGGRESSIVE_ENTRY_STAGE_COUNT = 7; // 積極的参加度は7段階評価
    const surveyCountMap: Map<number, SurveyAnalysisTabelMapping> = new Map();
    const nameMap = this.activityNameMap;

    this.answers.filter((sa: SurveyAnswer) => sa.type === type).map((sa: SurveyAnswer) => sa.activityAnswers.map((sac: SurveyActivityAnswer) => allActivityAnswers.push(sac)));

    nameMap.forEach((_, key) => {
      surveyCountMap.set(key, new SurveyAnalysisTabelMapping().deserialize({ id: key, stageCount: AGGRESSIVE_ENTRY_STAGE_COUNT, size: this.totalUser }));
    });

    allActivityAnswers.map((an: SurveyActivityAnswer) => {
      // アクティビティが削除されている場合もあるので、回答に紐付いたアクティビティIDに対応するデータが存在するとは限らないので判定している
      const surveyCount = surveyCountMap.get(an.activityID);
      if (surveyCount) surveyCount.countUpdateByRatio(an.score);
    });

    // ％に変換
    surveyCountMap.forEach((value: SurveyAnalysisTabelMapping) => {
      value.trancePercent();
    });

    return surveyCountMap;
  }

  @Action({ rawError: true })
  async getCompetencyAnswerTabels(): Promise<Map<number, SurveyAnalysisTabelMapping>> {
    const allCompetencyAnswers: SurveyCompetencyAnswer[] = new Array();
    // 事後のみ
    const type: number = 2;
    // 7 段階
    const stageCount = 7;
    const surveyCountMap: Map<number, SurveyAnalysisTabelMapping> = new Map();
    const nameMap = this.metaAwarenessNameMap;

    this.answers.filter((sa: SurveyAnswer) => sa.type === type).map((sa: SurveyAnswer) => sa.competencyAnswers.map((s: SurveyCompetencyAnswer) => allCompetencyAnswers.push(s)));

    nameMap.forEach((_, key) => {
      surveyCountMap.set(key, new SurveyAnalysisTabelMapping().deserialize({ id: key, stageCount: stageCount, size: this.totalUser }));
    });

    allCompetencyAnswers.map((s: SurveyCompetencyAnswer) => {
      const surveyCount = surveyCountMap.get(s.competencyID);
      if (surveyCount) surveyCount.countUpdateByRatio(s.score);
    });

    // ％に変換
    surveyCountMap.forEach((value: SurveyAnalysisTabelMapping) => {
      value.trancePercent();
    });

    return surveyCountMap;
  }

  /**
   * Map<number, Map<number, SurveyAnalysisTabelMapping>>
   * Map<competencyID,Map<acitivyID,SurveyAnalysisTabelMapping>> を返却する
   */
  @Action({ rawError: true })
  async getActivitySelectAnswerTabels(): Promise<Map<number, Map<number, SurveyAnalysisTabelMapping>>> {
    const allSelectAnswers: SurveyCompetencySelectActivity[] = new Array();
    // 事後のみ
    const type: number = 2;
    // 1 段階
    const stageCount = 1;
    const surveyCountMap: Map<number, Map<number, SurveyAnalysisTabelMapping>> = new Map();

    this.answers.filter((sa: SurveyAnswer) => sa.type === type).map((sa: SurveyAnswer) => sa.selectActivities.map((s: SurveyCompetencySelectActivity) => allSelectAnswers.push(s)));

    this.competencyNameMap.forEach((_, key1) => {
      let currentMap: Map<number, SurveyAnalysisTabelMapping> = new Map();
      surveyCountMap.set(key1, currentMap);
      this.activityNameMap.forEach((_, key2) => {
        currentMap.set(key2, new SurveyAnalysisTabelMapping().deserialize({ id: key2, stageCount: stageCount, size: this.totalUser }));
      });
    });

    allSelectAnswers.map((s: SurveyCompetencySelectActivity) => {
      // アクティビティが削除されている場合もあるので、回答に紐付いたアクティビティIDに対応するデータが存在するとは限らないので判定している
      const surveyCount = surveyCountMap.get(s.competencyID).get(s.activityID);
      if (surveyCount) surveyCount.countUpdateByRatio(100);
    });

    // ％に変換
    surveyCountMap.forEach((value1: any) => {
      value1.forEach((value2: any) => {
        value2.trancePercent();
      });
    });

    return surveyCountMap;
  }

  @Action({ rawError: true })
  async getExpectedAggressiveTable(): Promise<Map<number, ActiveEntryMatrix[]>> {
    const activeEntryMatrixMap: Map<number, ActiveEntryMatrix[]> = new Map();

    this.activeEntryMatrices.map(async (mtx: ActiveEntryMatrix) => {
      if ((await this.getTotalFromMatrix(mtx)) > 0) {
        if (!activeEntryMatrixMap.get(mtx.activityID)) {
          activeEntryMatrixMap.set(mtx.activityID, new Array<ActiveEntryMatrix>(mtx));
        } else {
          activeEntryMatrixMap.get(mtx.activityID).push(mtx);
        }
      }
    });

    return activeEntryMatrixMap;
  }

  @Action({ rawError: true })
  async fetchCompetencyPlusGrowthTable(): Promise<void> {
    const tmpMatrixMap: Map<number, ActiveEntryMatrix[]> = new Map();
    this.competencyPlusMatrices.map(async (mtx: ActiveEntryMatrix) => {
      if ((await this.getTotalFromMatrix(mtx)) > 0) {
        if (!tmpMatrixMap.get(mtx.activityID)) {
          tmpMatrixMap.set(mtx.activityID, new Array<ActiveEntryMatrix>(mtx));
        } else {
          tmpMatrixMap.get(mtx.activityID).push(mtx);
        }
      }
    });

    this.setCompetencyPlusMatrixMap(tmpMatrixMap);
  }

  @Action({ rawError: true })
  async getTotalFromMatrix(matrix: ActiveEntryMatrix): Promise<number> {
    return matrix.underUnder + matrix.underOver + matrix.overUnder + matrix.overOver;
  }

  @Action({ rawError: true })
  clean() {
    this.setEvent(new Event());
    this.setEventID(0);
    this.setAnswers([]);
    this.setActivityNameMap(new Map());
    this.setMetaAwarenessNameMap(new Map());
    this.setCompetencyNameMap(new Map());
    this.setSurveyAnalysisSelectTabelMap(new Map());
    this.setActiveEntryMatrices([]);
    this.setCompetencyPlusMatrices([]);
  }

  @Action({ rawError: true })
  makeActivityMap(activities: Activity[]) {
    activities.map((a: Activity) => this.activityNameMap.set(a.activityID, a.activityName));
  }

  @Action({ rawError: true })
  makeCompetencyMap(competencies: Competency[]) {
    competencies.map((a: Competency) => this.competencyNameMap.set(a.competencyID, a.name));
  }

  @Action({ rawError: true })
  makeMetaAwarenessNameMap(metaAwarenessGrowthQuestions: MetaAwarenessGrowthQuestion[]) {
    metaAwarenessGrowthQuestions.map((m: MetaAwarenessGrowthQuestion) => this.metaAwarenessNameMap.set(m.competencyID, m.questionText));
  }

  @Action({ rawError: true })
  async fetchCompetencyPulsAnalysis() {
    this.setCompetencyPlusMatrices(await surveyAnalyzeService.getListCompetencyPulsAnalysis(this.eventID, this.beforeEvalSetting, this.afterEvalSetting));
  }
}

export const surveyAnalyzeModule = getModule(SurveyAnalyzeModule);
